import { useState, useEffect, useRef } from "react";
import { useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import { ArrowLeft, CreditCard, Check, Upload, Bank, Phone, Copy, CheckCircle, FileText, Image as ImageIcon } from "@phosphor-icons/react";
import { toast } from "sonner";
import axios from "axios";
import { useAuth, API } from "../App";
import { Button } from "../components/ui/button";

const Payment = () => {
  const navigate = useNavigate();
  const { user, fetchProfile } = useAuth();
  const [plans, setPlans] = useState([]);
  const [paymentInfo, setPaymentInfo] = useState(null);
  const [selectedPlan, setSelectedPlan] = useState("mensal");
  const [step, setStep] = useState(1);
  const [uploading, setUploading] = useState(false);
  const [proofFile, setProofFile] = useState(null);
  const [proofPreview, setProofPreview] = useState(null);
  const [proofType, setProofType] = useState("image");
  const [copiedField, setCopiedField] = useState(null);
  const fileInputRef = useRef(null);

  useEffect(() => {
    fetchPlans();
  }, []);

  const fetchPlans = async () => {
    try {
      const response = await axios.get(`${API}/payments/plans`);
      setPlans(response.data.plans);
      setPaymentInfo(response.data.payment_info);
    } catch (error) {
      console.error('Error fetching plans:', error);
    }
  };

  const copyToClipboard = (text, field) => {
    navigator.clipboard.writeText(text);
    setCopiedField(field);
    toast.success("Copiado!");
    setTimeout(() => setCopiedField(null), 2000);
  };

  const handleFileSelect = (e) => {
    const file = e.target.files[0];
    if (!file) return;

    // Check file type
    const isPdf = file.type === 'application/pdf';
    const isImage = file.type.startsWith('image/');

    if (!isPdf && !isImage) {
      toast.error("Por favor, envia uma imagem ou PDF");
      return;
    }

    // Check file size (max 5MB)
    if (file.size > 5 * 1024 * 1024) {
      toast.error("Ficheiro muito grande. Máximo 5MB");
      return;
    }

    setProofType(isPdf ? "pdf" : "image");

    const reader = new FileReader();
    reader.onloadend = () => {
      setProofFile(reader.result);
      if (isImage) {
        setProofPreview(reader.result);
      } else {
        setProofPreview(null);
      }
    };
    reader.readAsDataURL(file);
  };

  const handleSubmitPayment = async () => {
    if (!proofFile) {
      toast.error("Por favor, anexa o comprovativo");
      return;
    }

    setUploading(true);
    try {
      await axios.post(`${API}/payments`, {
        plan: selectedPlan,
        proof_file_base64: proofFile,
        file_type: proofType
      });

      toast.success("Comprovativo enviado! Aguarde validação (até 24h úteis).");
      await fetchProfile();
      navigate('/');
    } catch (error) {
      toast.error(error.response?.data?.detail || "Erro ao enviar comprovativo");
    } finally {
      setUploading(false);
    }
  };

  const selectedPlanData = plans.find(p => p.id === selectedPlan);

  return (
    <div className="min-h-screen bg-background">
      {/* Header */}
      <header className="bg-primary text-white p-4 flex items-center gap-4">
        <button 
          onClick={() => navigate(-1)}
          className="p-2 hover:bg-white/10 rounded-full transition-colors"
          data-testid="back-button"
        >
          <ArrowLeft size={24} weight="bold" />
        </button>
        <h1 className="text-xl font-bold">Assinatura</h1>
      </header>

      <main className="p-6 pb-24">
        {step === 1 && (
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            className="space-y-6"
          >
            {/* Trial info */}
            {user?.subscription_status === 'trial' && (
              <div className="bg-primary/10 border border-primary/20 rounded-2xl p-4 text-center">
                <p className="font-bold text-primary">Teste gratuito de 7 dias</p>
                <p className="text-sm text-muted-foreground">
                  Desbloqueia todas as funcionalidades com um plano
                </p>
              </div>
            )}

            {/* Payment Pending Notice */}
            {user?.payment_pending && (
              <div className="bg-zest/10 border border-zest/20 rounded-2xl p-4 text-center">
                <p className="font-bold text-zest">⏳ Pagamento em análise</p>
                <p className="text-sm text-muted-foreground">
                  O teu comprovativo está a ser validado. Aguarda até 24h úteis.
                </p>
              </div>
            )}

            <h2 className="text-2xl font-bold text-foreground">Escolhe o teu plano</h2>

            {/* Plans */}
            <div className="space-y-4">
              {plans.map((plan) => (
                <motion.button
                  key={plan.id}
                  onClick={() => setSelectedPlan(plan.id)}
                  className={`w-full p-5 rounded-2xl border-2 text-left transition-all relative ${
                    selectedPlan === plan.id 
                      ? "border-primary bg-primary/10" 
                      : "border-muted bg-white hover:border-primary/50"
                  }`}
                  whileTap={{ scale: 0.98 }}
                  data-testid={`plan-${plan.id}`}
                >
                  {plan.savings && (
                    <span className="absolute -top-3 right-4 bg-zest text-white text-xs font-bold px-3 py-1 rounded-full">
                      -{plan.savings}
                    </span>
                  )}
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="font-bold text-lg text-foreground">{plan.name}</p>
                      <p className="text-sm text-muted-foreground">{plan.period}</p>
                    </div>
                    <div className="text-right">
                      <p className="text-2xl font-bold text-primary">{plan.price_formatted}</p>
                    </div>
                  </div>
                  {selectedPlan === plan.id && (
                    <div className="absolute top-1/2 -translate-y-1/2 -left-3">
                      <div className="w-6 h-6 bg-primary rounded-full flex items-center justify-center">
                        <Check size={16} className="text-white" weight="bold" />
                      </div>
                    </div>
                  )}
                </motion.button>
              ))}
            </div>

            {/* Features */}
            <div className="bg-white rounded-2xl p-5 shadow-card">
              <h3 className="font-bold text-foreground mb-4">O que inclui:</h3>
              <ul className="space-y-3">
                {[
                  "📸 Câmera IA para análise de comida",
                  "🍲 Reconhecimento de pratos angolanos",
                  "📊 Relatórios semanais detalhados",
                  "💪 Sugestões de treino personalizadas",
                  "💬 Acesso ao Grupo VIP WhatsApp",
                  "🎯 Recálculo automático de plateau"
                ].map((feature, i) => (
                  <li key={i} className="flex items-center gap-3 text-foreground">
                    <CheckCircle size={20} className="text-primary" weight="fill" />
                    <span>{feature}</span>
                  </li>
                ))}
              </ul>
            </div>

            <Button
              onClick={() => setStep(2)}
              className="btn-primary w-full"
              data-testid="continue-to-payment"
            >
              Continuar - {selectedPlanData?.price_formatted}
            </Button>
          </motion.div>
        )}

        {step === 2 && (
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            className="space-y-6"
          >
            <button 
              onClick={() => setStep(1)}
              className="text-primary font-medium flex items-center gap-2"
            >
              <ArrowLeft size={20} />
              Voltar aos planos
            </button>

            <h2 className="text-2xl font-bold text-foreground">Efetuar Pagamento</h2>

            {/* Multicaixa Express - Primary */}
            <div className="bg-primary text-white rounded-2xl p-5">
              <div className="flex items-center gap-3 mb-4">
                <Phone size={28} weight="duotone" />
                <h3 className="font-bold text-lg">Pagar via Multicaixa Express</h3>
              </div>
              <p className="text-white/80 mb-4">Transfere para o número:</p>
              <div className="bg-white/10 rounded-xl p-4 flex items-center justify-between">
                <span className="text-2xl font-bold font-mono">{paymentInfo?.multicaixa_number}</span>
                <button
                  onClick={() => copyToClipboard(paymentInfo?.multicaixa_number, 'multicaixa')}
                  className="p-2 hover:bg-white/10 rounded-lg transition-colors"
                  data-testid="copy-multicaixa"
                >
                  {copiedField === 'multicaixa' ? (
                    <Check size={24} weight="bold" />
                  ) : (
                    <Copy size={24} />
                  )}
                </button>
              </div>
              <p className="text-white/80 mt-3 text-sm">
                Valor: <span className="font-bold">{selectedPlanData?.price_formatted}</span>
              </p>
            </div>

            {/* IBAN - Alternative */}
            <div className="bg-white rounded-2xl p-5 shadow-card">
              <div className="flex items-center gap-3 mb-4">
                <Bank size={28} className="text-primary" weight="duotone" />
                <h3 className="font-bold text-lg text-foreground">Ou via Transferência Bancária</h3>
              </div>
              
              <div className="space-y-3">
                <div>
                  <p className="text-sm text-muted-foreground mb-1">IBAN</p>
                  <div className="bg-muted/50 rounded-xl p-3 flex items-center justify-between">
                    <span className="font-mono text-foreground text-sm">{paymentInfo?.iban}</span>
                    <button
                      onClick={() => copyToClipboard(paymentInfo?.iban, 'iban')}
                      className="p-2 hover:bg-muted rounded-lg transition-colors"
                      data-testid="copy-iban"
                    >
                      {copiedField === 'iban' ? (
                        <Check size={20} className="text-primary" weight="bold" />
                      ) : (
                        <Copy size={20} className="text-muted-foreground" />
                      )}
                    </button>
                  </div>
                </div>
                
                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <p className="text-sm text-muted-foreground mb-1">Banco</p>
                    <p className="font-medium text-foreground">{paymentInfo?.bank}</p>
                  </div>
                  <div>
                    <p className="text-sm text-muted-foreground mb-1">Titular</p>
                    <p className="font-medium text-foreground">{paymentInfo?.holder}</p>
                  </div>
                </div>
              </div>
            </div>

            {/* Upload Proof */}
            <div className="bg-white rounded-2xl p-5 shadow-card">
              <h3 className="font-bold text-foreground mb-2">
                📎 Enviar Comprovativo
              </h3>
              <p className="text-sm text-muted-foreground mb-4">
                Aceita imagens (JPG, PNG) ou PDF. Máximo 5MB.
              </p>
              
              <input
                ref={fileInputRef}
                type="file"
                accept="image/*,.pdf"
                onChange={handleFileSelect}
                className="hidden"
              />

              {proofFile ? (
                <div className="space-y-3">
                  {proofType === 'image' && proofPreview ? (
                    <img 
                      src={proofPreview} 
                      alt="Comprovativo" 
                      className="w-full h-48 object-cover rounded-xl"
                    />
                  ) : (
                    <div className="w-full h-32 bg-muted/50 rounded-xl flex flex-col items-center justify-center gap-2">
                      <FileText size={48} className="text-primary" weight="duotone" />
                      <span className="text-sm text-muted-foreground">Ficheiro PDF selecionado</span>
                    </div>
                  )}
                  <button
                    onClick={() => fileInputRef.current?.click()}
                    className="text-primary font-medium text-sm"
                  >
                    Trocar ficheiro
                  </button>
                </div>
              ) : (
                <button
                  onClick={() => fileInputRef.current?.click()}
                  className="w-full h-32 border-2 border-dashed border-primary/30 rounded-2xl flex flex-col items-center justify-center gap-2 hover:bg-primary/5 transition-colors"
                  data-testid="upload-proof-button"
                >
                  <Upload size={32} className="text-primary" weight="duotone" />
                  <span className="text-primary font-medium">Carregar Comprovativo</span>
                  <span className="text-xs text-muted-foreground">Imagem ou PDF</span>
                </button>
              )}
            </div>

            <Button
              onClick={handleSubmitPayment}
              disabled={uploading || !proofFile}
              className="btn-primary w-full disabled:opacity-50"
              data-testid="submit-payment-button"
            >
              {uploading ? "A enviar..." : "Enviar Comprovativo"}
            </Button>
          </motion.div>
        )}
      </main>
    </div>
  );
};

export default Payment;
